import { Injectable } from '@angular/core';
import { Role } from '../enums';
import { buildMenuGroups, NavigationContext } from '../navigation-helper';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private hierarchy: Role[] = [
    Role.Administrator,
    Role.Planner,
    Role.RidePlanner,
    Role.Driver,
    Role.CoDriver,
    Role.Other,
  ];

  /**
   * Returns the highest role from an array of roles based on the hierarchy.
   */
  getHighestRole(userOrRoles: any | undefined): Role | undefined {
    if (!userOrRoles) return undefined;

    let roles: Role[];
    let user: any;

    if (Array.isArray(userOrRoles)) {
      roles = userOrRoles;
    } else {
      user = userOrRoles;
      roles = user.roles ?? [];
    }

    for (const role of this.hierarchy) {
      if (roles.includes(role)) return role;
    }

    return roles[0];
  }

  /**
   * Checks if the user has at least the required role or higher.
   */
  hasAtLeastRole(userRoles: Role[] | undefined, requiredRole: Role): boolean {
    const highestRole = this.getHighestRole(userRoles);
    if (!highestRole) return false;

    const highestRoleIndex = this.hierarchy.indexOf(highestRole);
    const requiredRoleIndex = this.hierarchy.indexOf(requiredRole);

    return highestRoleIndex !== -1 && highestRoleIndex <= requiredRoleIndex;
  }

  /**
   * Checks if the user has a specific role.
   */
  hasRole(userRoles: Role[] | undefined, targetRole: Role): boolean {
    if (!userRoles) return false;
    return userRoles.includes(targetRole);
  }

  getDefaultRoute(
    userId: string,
    roles: Role[] | undefined,
    context: Omit<NavigationContext, 'roles' | 'userId'> = {},
  ): string {
    if (!roles || roles.length === 0) {
      return '/selectingOrganisation';
    }

    const navigationContext: NavigationContext = {
      ...context,
      userId,
      roles,
    };
    const menuLinks = buildMenuGroups(navigationContext, this)
      .flatMap((group) => group.items)
      .map((item) => item.link);
    const preferredRoute = this.getPreferredRoute(userId, roles);

    if (preferredRoute && menuLinks.includes(preferredRoute)) {
      return preferredRoute;
    }

    return menuLinks[0] ?? '/account';
  }

  private getPreferredRoute(userId: string, roles: Role[]): string | undefined {
    const highestRole = this.getHighestRole(roles);
    switch (highestRole) {
      case Role.Administrator:
      case Role.Planner:
      case Role.RidePlanner:
        return '/plannen';
      case Role.Driver:
        return `/schedule/driver/${userId}`;
      case Role.CoDriver:
        return `/schedule/co-driver/${userId}`;
      case Role.Other:
        return undefined;
      default:
        return undefined;
    }
  }
}
